import { Children, cloneElement, isValidElement, type ReactNode } from "react";
import { DEFAULT_LOCALE } from "@/lib/i18n-config";
import { useI18n } from "./I18nContext";

// The pages converted from PHP hold their copy as literal JSX text, not props,
// so there is nothing to translate in getStaticProps. This walks the rendered
// element tree instead and swaps text nodes and user-visible attributes for
// their Hindi equivalents.
//
// It runs during static export only (the pages are prerendered once), so the
// recursion cost is paid at build time, not in the browser.

/** Attributes a reader can actually see. Everything else is left alone. */
const TRANSLATABLE_ATTRS = ["alt", "title", "placeholder", "aria-label", "aria-description"];

/** Elements whose contents are code or markup, never prose. */
const OPAQUE_TAGS = new Set(["script", "style", "code", "pre", "iframe", "svg"]);

function translateNode(node: ReactNode, t: (text: string) => string): ReactNode {
  if (typeof node === "string") return t(node);
  if (typeof node === "number" || node == null || typeof node === "boolean") return node;

  if (Array.isArray(node)) {
    return Children.map(node, (child) => translateNode(child, t));
  }

  if (!isValidElement(node)) return node;

  const type = node.type;
  const tag = typeof type === "string" ? type.toLowerCase() : "";

  // Never touch raw HTML injected via dangerouslySetInnerHTML: it is CMS body
  // copy or a stylesheet, and rewriting it would risk breaking the markup.
  const props = node.props as Record<string, any>;
  if (props?.dangerouslySetInnerHTML || OPAQUE_TAGS.has(tag)) return node;

  const nextProps: Record<string, any> = {};
  let changed = false;

  for (const attr of TRANSLATABLE_ATTRS) {
    const value = props?.[attr];
    if (typeof value === "string") {
      const translated = t(value);
      if (translated !== value) {
        nextProps[attr] = translated;
        changed = true;
      }
    }
  }

  const children = props?.children;
  if (children !== undefined) {
    nextProps.children = translateNode(children, t);
    changed = true;
  }

  return changed ? cloneElement(node, nextProps) : node;
}

/**
 * Wraps a page so its visible English text renders in `locale`. A no-op for
 * English, so the default routes stay byte-identical.
 */
export default function TranslateTree({
  locale,
  children,
}: {
  locale: string;
  children: ReactNode;
}) {
  const { t } = useI18n();
  if (locale === DEFAULT_LOCALE) return <>{children}</>;
  return <>{translateNode(children, t)}</>;
}
